/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/store/auth.ts
 * PURPOSE: State management and data store
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P4 (Quality Gates - Security) + P1 (Stage 06)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * CTO MANDATE: Production-grade authentication with multi-tenant security
 *
 * @business-context
 * - Domain: State Management, Global State, Vietnamese F&B Application State
 * - Impact: State Consistency, Performance, Developer Experience
 * - Stakeholders: Development Team, Frontend Engineers
 *
 * @technical-architecture
 * - Layer: State Management Layer, Global Store
 * - Pattern: Redux/Zustand Pattern, State Slices, Actions
 * - Dependencies: Redux Toolkit/Zustand, TypeScript, Immer
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { User } from "../types/auth";

/**
 * Configurable auth storage keys - used by apiClient and other services
 * Centralized here to avoid hardcoded strings across codebase
 */
export const AUTH_STORAGE_KEYS = {
  /** Key for Zustand persist storage (JSON object) */
  ZUSTAND_STORE: "auth-storage",
  /** Key for raw access token - used by apiClient interceptor */
  ACCESS_TOKEN: "access_token",
  /** Key for raw refresh token - used by apiClient interceptor */
  REFRESH_TOKEN: "refresh_token",
  /** Key for platform admin access token — detect refresh route (CTO F8) */
  PLATFORM_ADMIN_TOKEN: "platform_admin_access_token",
} as const;

interface AuthState {
  // State
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  // Sprint 52 (S52-006): Three-Tier Role Architecture fields (CTO T2, G1)
  tenantKey: string | null;
  capabilities: string[];
  userRole: string | null;
  /**
   * SC-001-LOGIN §C (WIREFRAME-V0-SCREEN-SPEC-MAP.md) — server-resolved post-login
   * route from the login response (`home_route`). Persisted so DashboardGuard
   * (frontend/src/App.tsx) can prefer it over recomputing from userRole on a
   * direct /dashboard hit. Null for sessions logged in before this field existed —
   * consumers must fall back to utils/roleDashboardMap.ts in that case.
   */
  homeRoute: string | null;

  // Actions
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: User) => void;
  /** Sprint 52: Set tenant context from login response (+ SC-001-LOGIN: home_route) */
  setTenantAuth: (tenantKey: string, capabilities: string[], userRole: string, homeRoute?: string | null) => void;
  logout: () => void;
  setLoading: (isLoading: boolean) => void;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

// useAuthStore__processes_logic__returns_result
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      // Initial state
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      // Sprint 52 (S52-006): Three-Tier fields
      tenantKey: null,
      capabilities: [],
      userRole: null,
      homeRoute: null,

      // Actions
      setTokens: (accessToken, refreshToken) => {
        // Sync to localStorage for apiClient interceptor
        localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, accessToken);
        localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
        set({
          accessToken,
          refreshToken,
          isAuthenticated: true,
        });
      },

      setUser: (user) => set({ user }),

      setTenantAuth: (tenantKey, capabilities, userRole, homeRoute = null) => {
        set({ tenantKey, capabilities, userRole, homeRoute });
      },

      logout: () => {
        // Clear synced localStorage keys
        localStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          tenantKey: null,
          capabilities: [],
          userRole: null,
          homeRoute: null,
        });
      },

      setLoading: (isLoading) => set({ isLoading }),

      changePassword: async (currentPassword, newPassword) => {
        /**
         * Change user password (Sprint 40 Gap 1 - P0)
         *
         * Implementation notes:
         * - Backend increments token_version on successful password change
         * - This invalidates all existing JWT tokens (session invalidation)
         * - #1007: response carries a fresh token pair at the new version; adopt it
         *   (this device stays signed in, other devices stay revoked)
         * - Backend validates: min 8 chars, 1 number, 1 upper, 1 lower
         *   (NO special char requirement for Vietnamese keyboards)
         *
         * @cross-references
         * - Backend API: backend/app/api/v1/auth.py (lines 573-597)
         * - Test Plan: docs/05-test/02-Test-Plans/COMPREHENSIVE-TEST-PLAN-APPROVED-JAN27-2026.md
         * - Gap 1 Details: Line 854-867 (P0 - 2 SP)
         * - Phase 2.1 Test: Tenant Admin First Login
         */
        const { accessToken, setTokens } = useAuthStore.getState();

        if (!accessToken) {
          throw new Error('Not authenticated');
        }

        try {
          const response = await fetch('/api/v1/auth/change-password', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              old_password: currentPassword,
              new_password: newPassword,
            }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Password change failed');
          }

          // #1007: backend bumped token_version (every old token, this one included, is
          // now 401) and returned a fresh pair minted at the new version. Adopt it so
          // this device stays signed in (first-login forced change too); other
          // devices stay revoked. must_change_password is cleared server-side.
          const data = await response.json();
          setTokens(data.access_token, data.refresh_token);
          const { user } = useAuthStore.getState();
          if (user) set({ user: { ...user, must_change_password: false } });
        } catch (error) {
          // Re-throw for ChangePassword component to handle
          throw error;
        }
      },
    }),
    {
      name: AUTH_STORAGE_KEYS.ZUSTAND_STORE,
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        tenantKey: state.tenantKey,
        capabilities: state.capabilities,
        userRole: state.userRole,
        homeRoute: state.homeRoute,
      }),
      // Sync tokens to localStorage on rehydrate (page refresh)
      onRehydrateStorage: () => (state) => {
        if (state?.accessToken) {
          localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, state.accessToken);
        }
        if (state?.refreshToken) {
          localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, state.refreshToken);
        }
      },
    },
  ),
);
